<template>
  <BasePopup
    :show="show"
    scrollable
    close-test-id="vc-close"
    card-class="w-[550px] max-w-full p-6 pt-4 pb-0 md:pb-6"
    @close="$emit('close')"
  >
    <div class="flex flex-col" :class="success ? 'h-[350px]' : 'min-h-[585px]'">
      <div class="flex items-center mb-5 pr-10">
        <h3 v-if="!success" class="text-[20px] font-bold text-black tracking-[-0.01em]">Prenota una videochiamata gratuita</h3>
      </div>

      <!-- Success — keeps the form's dimensions; content centered in the box -->
      <div v-if="success" data-testid="vc-success" class="space-y-4 text-center flex flex-col items-center justify-center flex-1">
        <svg
          data-testid="vc-success-icon"
          class="mx-auto h-14 w-14 text-[#489B79]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M8 12.5l2.5 2.5L16 9" />
        </svg>
        <p class="text-base font-semibold text-black">Richiesta inviata!</p>
        <p class="text-sm text-gray-700">
          Abbiamo ricevuto la tua richiesta. Un progettista ti contatterà il prima
          possibile per fissare l'appuntamento.
        </p>
        <button
          type="button"
          class="w-full rounded-full px-6 py-3 bg-[#489B79] text-white font-bold"
          @click="$emit('close')"
        >
          Chiudi
        </button>
      </div>

      <!-- Booking form -->
      <div v-else class="space-y-5">
        <div data-issue-field="days" :class="issueClass('days')">
          <h4 class="text-[15px] leading-[26px] font-semibold text-black">Giorni preferiti della settimana</h4>
          <p class="text-[13px] leading-[26px] font-medium text-[#575757] mb-2">Selezionane più di uno!</p>
          <chip-multi-select :options="days" v-model="selectedDays" />
          <form-issue :message="issueFor('days')" test-id="vc-issue-days" />
        </div>

        <div data-issue-field="slots" :class="issueClass('slots')">
          <h4 class="text-[15px] leading-[26px] font-semibold text-black mb-2">Fascia oraria preferita</h4>
          <chip-multi-select :options="timeSlots" v-model="selectedTimeSlots" />
          <form-issue :message="issueFor('slots')" test-id="vc-issue-slots" />
        </div>

        <div>
          <h4 class="text-[15px] leading-[26px] font-semibold text-black mb-2">Il tuo obiettivo</h4>
          <objective-select :options="objectives" :single="singleObjective" v-model="selectedObjectives" />
        </div>

        <div v-if="showGeneralLink" data-issue-field="link" :class="issueClass('link')">
          <h4 class="text-[15px] leading-[26px] font-semibold text-black">{{ linkHeading }}</h4>
          <p class="text-[13px] leading-[26px] font-medium text-[#575757] mb-2">{{ linkHelp }}</p>
          <input
            data-testid="vc-link"
            v-model="generalLink"
            type="url"
            inputmode="url"
            placeholder="Incolla qui il link dell'annuncio"
            class="ic-field__input"
          />
          <form-issue :message="issueFor('link')" test-id="vc-issue-link" />
        </div>

        <div data-issue-field="phone" :class="issueClass('phone')">
          <h4 class="text-[15px] leading-[26px] font-semibold text-black">Conferma il telefono</h4>
          <p class="text-[13px] leading-[26px] font-medium text-[#575757] mb-2">Il progettista ti contatterà a questo numero</p>
          <div class="flex gap-3">
            <!-- Prefisso internazionale (stessa UI del popup Richiedi visita) -->
            <div class="relative">
              <select
                data-testid="vc-phone-code"
                v-model="phoneCode"
                class="ic-field__input na-phone-code"
              >
                <option value="+39">+39</option>
                <option value="+1">+1</option>
                <option value="+33">+33</option>
                <option value="+49">+49</option>
              </select>
              <small class="absolute left-3 top-0 na-phone-code-title">Prefisso</small>
            </div>
            <!-- Solo cifre: strip di ogni carattere non numerico in input. -->
            <input
              data-testid="vc-phone"
              :value="phone"
              @input="onPhoneInput"
              type="tel"
              inputmode="numeric"
              autocomplete="tel-national"
              class="flex-1 ic-field__input"
            />
          </div>
          <form-issue :message="issueFor('phone')" test-id="vc-issue-phone" />
        </div>

        <div>
          <h4 class="text-[15px] leading-[26px] font-semibold text-black mb-2">Note</h4>
          <textarea
            data-testid="vc-notes"
            v-model="notes"
            rows="3"
            maxlength="1000"
            placeholder="Vuoi aggiungere delle informazioni?"
            class="ic-field__input resize-none"
          ></textarea>
        </div>

        <!-- Consenso privacy (#723): salvato sulla richiesta in privacyConsent. -->
        <div data-issue-field="privacy" :class="issueClass('privacy')">
          <privacy-consent-checkbox v-model="privacy" test-id="vc-privacy" />
          <form-issue :message="issueFor('privacy')" test-id="vc-issue-privacy" />
        </div>
        <!-- Passaggio a ristrutturazione (#3234): obbligatoria sulle pagine Immaginacasa. -->
        <div v-if="needsShare" data-issue-field="share" :class="issueClass('share')">
          <share-consent-checkbox v-model="shareConsent" test-id="vc-share" />
          <form-issue :message="issueFor('share')" test-id="vc-issue-share" />
        </div>

        <p v-if="errorMsg" data-testid="vc-error" class="text-sm text-red-600">{{ errorMsg }}</p>

        <div class="flex justify-end sticky bottom-0 z-10 -mx-6 px-6 pt-4 pb-4 bg-white md:static md:mx-0 md:px-0 md:pt-0 md:pb-0">
          <button
            type="button"
            data-testid="vc-confirm"
            :disabled="submitting"
            class="rounded-[9px] min-w-[110px] h-[45px] px-6 text-[15px] bg-[#489B79] text-white font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2"
            @click="confirm"
          >
            <span>{{ submitting ? "Invio in corso..." : "Invia richiesta" }}</span>
            <img v-if="!submitting" :style="{
              'width': '15px',
              'height': '12px',
              'background-color': '#fff',
              'mask': 'url(\'' + require('@/assets/img/icons/arrow-next.svg') + '\') no-repeat center / contain',
              '-webkit-mask': 'url(\'' + require('@/assets/img/icons/arrow-next.svg') + '\') no-repeat center / contain'
            }">
          </button>
        </div>
      </div>
    </div>
  </BasePopup>
</template>

<script>
import firebase from "firebase/compat/app";
import { db, functions } from "@/firebase/init.js";
import { httpsCallable } from "firebase/functions";
import { trackEvent } from "@/services/analytics";
import { trackAdContact } from "@/services/stats";
import { globalStore, updateUserInfo, currentBrand } from "@/globalStore.js";
import BasePopup from "@/components/BasePopup.vue";
import ChipMultiSelect from "@/components/Videocall/ChipMultiSelect.vue";
import ObjectiveSelect from "@/components/Videocall/ObjectiveSelect.vue";
import PrivacyConsentCheckbox from "@/components/forms/PrivacyConsentCheckbox.vue";
import { buildPrivacyConsent } from "@/consent/privacyConsent.js";
import ShareConsentCheckbox from "@/components/forms/ShareConsentCheckbox.vue";
import { buildShareConsent, needsShareConsent } from "@/consent/shareConsent.js";
import FormIssue from "@/components/forms/FormIssue.vue";
import { ISSUE_MESSAGES, formIssue, formIssueMixin } from "@/utils/formIssues.js";
import {
  VIDEOCALL_DAYS,
  VIDEOCALL_TIME_SLOTS,
  VIDEOCALL_GENERAL_OBJECTIVE,
  VIDEOCALL_OBJECTIVES,
  createVideocallRequest,
  sendVideocallRequestEmails,
  sendGeneralVideocallRequestEmails,
  maybeSaveProfilePhone,
  localPhoneDigits,
  isValidLink,
  normalizeLink,
} from "@/services/videocall";

// The server's refusal of a request without consent (#723).
const PRIVACY_REQUIRED_MSG = ISSUE_MESSAGES.privacy;

export default {
  name: "BookVideocallPopup",
  components: { BasePopup, ChipMultiSelect, ObjectiveSelect, PrivacyConsentCheckbox, ShareConsentCheckbox, FormIssue },
  // #860: the button stays clickable and a click names what is missing.
  mixins: [formIssueMixin],
  props: {
    show: { type: Boolean, default: false },
    uid: { type: String, default: "" },
    clientName: { type: String, default: "" },
    clientEmail: { type: String, default: "" },
    defaultPhone: { type: String, default: "" },
    newarcHomeId: { type: String, default: "" },
    adCode: { type: String, default: "" },
    adAddress: { type: String, default: "" },
    objectives: { type: Array, default: () => VIDEOCALL_OBJECTIVES },
    linkObjective: { type: String, default: VIDEOCALL_GENERAL_OBJECTIVE },
    linkHeading: { type: String, default: "Hai già un immobile in mente?" },
    linkHelp: { type: String, default: "Incolla il link dell'annuncio (facoltativo)" },
    source: { type: String, default: "immaginacasa-ad" },
    generalEmails: { type: Boolean, default: false },
    singleObjective: { type: Boolean, default: false },
  },
  emits: ["close", "submitted"],
  computed: {
    // #3234: on an Immaginacasa page the request hands the client over to the
    // renovation partner, so the share consent is required.
    needsShare() {
      return needsShareConsent(currentBrand());
    },
    // The optional property link applies to the objective that unlocks it.
    showGeneralLink() {
      return this.selectedObjectives.includes(this.linkObjective);
    },
    // #860: the first missing thing, by the same rules (and order) as before.
    pendingIssue() {
      const noDays = this.selectedDays.length === 0;
      const noSlots = this.selectedTimeSlots.length === 0;
      if (noDays && noSlots) return formIssue("days", ISSUE_MESSAGES.daysAndSlots);
      if (noDays) return formIssue("days");
      if (noSlots) return formIssue("slots");
      if (!this.phone) return formIssue("phone");
      // Light validation: only when the field is shown and the user typed something.
      if (this.showGeneralLink && this.generalLink.trim() && !isValidLink(this.generalLink)) {
        return formIssue("link");
      }
      if (this.privacy !== true) return formIssue("privacy");
      if (this.needsShare && this.shareConsent !== true) return formIssue("share");
      return null;
    },
  },
  data() {
    return {
      days: VIDEOCALL_DAYS,
      timeSlots: VIDEOCALL_TIME_SLOTS,
      selectedDays: [],
      selectedTimeSlots: [],
      selectedObjectives: [],
      generalLink: "",
      notes: "",
      phoneCode: "+39",
      phone: localPhoneDigits(this.defaultPhone),
      privacy: false,
      shareConsent: false,
      submitting: false,
      success: false,
      errorMsg: "",
    };
  },
  watch: {
    defaultPhone(val) {
      if (!this.phone) this.phone = localPhoneDigits(val);
    },
    // Drop a stale link if the user removes the objective that exposes the field.
    selectedObjectives(val) {
      if (!val.includes(this.linkObjective)) this.generalLink = "";
    },
    show(val) {
      if (val) {
        this.success = false;
        this.errorMsg = "";
        this.issue = null;
        this.submitting = false;
        this.generalLink = "";
        this.notes = "";
        // Il consenso vale per una richiesta: a ogni apertura si rispunta (#723).
        this.privacy = false;
        this.shareConsent = false;
        if (!this.phone) this.phone = localPhoneDigits(this.defaultPhone);
      }
    },
  },
  methods: {
    // Keep the phone field digits-only, correcting the input element in place.
    onPhoneInput(e) {
      const digits = e.target.value.replace(/\D+/g, "");
      this.phone = digits;
      if (e.target.value !== digits) e.target.value = digits;
    },
    async confirm() {
      if (this.submitting) return;
      this.errorMsg = "";

      // #860: days, slots, phone, link, privacy, share: shown next to the field.
      if (this.blockOnIssue()) return;
      const fullPhone = `${this.phoneCode} ${this.phone}`.trim();

      // The rule only accepts a request written by the signed-in user for
      // itself, so the auth uid wins over the prop; without a session the
      // write would be refused, so say so instead of a generic error.
      const authUser = firebase.auth().currentUser;
      if (!authUser || !authUser.uid) {
        this.errorMsg = "La sessione è scaduta. Accedi di nuovo per inviare la richiesta.";
        return;
      }

      this.submitting = true;
      let requestId;
      try {
        requestId = await createVideocallRequest({
          db,
          uid: authUser.uid,
          clientName: this.clientName,
          clientEmail: this.clientEmail,
          clientPhone: fullPhone,
          newarcHomeId: this.newarcHomeId,
          adCode: this.adCode,
          adAddress: this.adAddress,
          preferredDays: this.selectedDays,
          preferredTimeSlots: this.selectedTimeSlots,
          objectives: this.selectedObjectives,
          notes: this.notes,
          generalPropertyLink: this.generalLink,
          source: this.source,
          // Costruito al momento dell'invio (#723).
          privacyConsent: buildPrivacyConsent(),
          ...(this.needsShare ? { shareConsent: buildShareConsent() } : {}),
        });
      } catch (e) {
        console.error("[videocall] request not saved:", (e && (e.code || e.message)) || e);
        this.submitting = false;
        this.errorMsg =
          e && e.message === "PRIVACY_REQUIRED" ? PRIVACY_REQUIRED_MSG : "Si è verificato un errore. Riprova.";
        return;
      }

      // If the profile had no phone, persist the one the user entered. Non-blocking.
      try {
        const saved = await maybeSaveProfilePhone({
          updateUserInfo,
          profilePhone: this.defaultPhone,
          enteredPhone: fullPhone,
        });
        if (saved && globalStore.newarcUser) {
          globalStore.newarcUser.phone = fullPhone;
        }
      } catch (e) {
        // non-blocking
      }

      // Emails are best-effort: the saved request is the source of truth.
      try {
        const call = httpsCallable(functions, "sendNotificationEmail");
        const sendEmail = (payload) => call(payload);
        if (this.generalEmails) {
          await sendGeneralVideocallRequestEmails({
            sendEmail,
            clientEmail: this.clientEmail,
            clientName: this.clientName,
            generalPropertyLink: this.showGeneralLink ? normalizeLink(this.generalLink) : "",
          });
        } else {
          await sendVideocallRequestEmails({
            sendEmail,
            adAddress: this.adAddress,
            adCode: this.adCode,
            clientEmail: this.clientEmail,
            clientName: this.clientName,
          });
        }
      } catch (e) {
        // non-blocking: request already saved
      }

      this.submitting = false;
      this.success = true;
      trackEvent("videocall_request", { ad_id: this.adId });
      // Statistiche di prima parte (#652): solo per le richieste legate a un
      // annuncio (newarcHomeId vuoto in modalità standalone → no-op).
      trackAdContact({ adId: this.newarcHomeId, contactType: "videocall" });
      this.$emit("submitted", requestId);
    },
  },
};
</script>
